import type { ReactNode } from "react";

import { Button } from "@/components/ui";
import type { NewFieldCheck } from "@/lib/attributeReview/newFieldCheck";
import { cn } from "@/lib/utils";

import { NameCheck, type NameState } from "./NameCheck";

type Props = {
  prefix: string;
  name: string;
  path: string;
  check: NewFieldCheck;
  likeLabel: string;
  autoFocus: boolean;
  pickedPath: string | null;
  store: string;
  kindControl: ReactNode;
  onName: (name: string) => void;
  onUse: (path: string) => void;
};

const STATE: Record<NewFieldCheck["state"], NameState> = {
  bad: "bad",
  exists: "info",
  similar: "warn",
  fresh: "ok",
};

const fieldLabel =
  "flex flex-col gap-1.5 text-xs text-slate-500 dark:text-slate-400";

export function NewFieldName({
  prefix,
  name,
  path,
  check,
  likeLabel,
  autoFocus,
  pickedPath,
  store,
  kindControl,
  onName,
  onUse,
}: Props) {
  const state: NameState = name === "" ? "none" : STATE[check.state];
  const blocked = state === "none" || state === "bad";
  const exists = check.state === "exists";
  return (
    <>
      <label className={fieldLabel}>
        Field name
        <span
          className={cn(
            "flex overflow-hidden rounded-lg border bg-white font-mono text-xs focus-within:border-indigo-400 dark:bg-slate-950",
            state === "bad"
              ? "border-rose-500/60"
              : "border-slate-300 dark:border-slate-700",
          )}
        >
          <span className="py-2 pl-2.5 text-slate-500">{prefix}</span>
          <input
            value={name}
            autoFocus={autoFocus}
            spellCheck={false}
            aria-label="New field name"
            onChange={(e) => onName(e.target.value)}
            className="min-w-0 flex-1 bg-transparent py-2 pr-2.5 text-slate-900 outline-none dark:text-slate-50"
          />
        </span>
      </label>
      <NameCheck
        state={state}
        similar={
          check.state === "similar"
            ? { label: likeLabel, onUse: () => onUse(check.like) }
            : undefined
        }
      />
      <div className={fieldLabel}>{kindControl}</div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {exists ? "Links to" : "Creates"}{" "}
        <span className="font-mono">{path}</span>.
        {exists
          ? " Other stores may use it."
          : ` Only ${store} uses it for now.`}
      </p>
      <Button
        size="sm"
        variant="outline"
        className="self-start"
        disabled={blocked || path === pickedPath}
        onClick={() => onUse(path)}
      >
        {path === pickedPath
          ? "Using this field"
          : exists
            ? "Use this field"
            : "Create and use this field"}
      </Button>
    </>
  );
}
